/**
 * "Can I?" for the client.
 *
 * Reads the permission list `/auth/me` already returns, so no component makes
 * its own request to find out what it is allowed to render.
 *
 * ── This is presentation, not security ───────────────────────────────────────
 *
 * Every route these gates lead to is guarded again on the server. Hiding a
 * button is a courtesy to the person using the app — it stops them walking
 * into a permission error — and nothing more. Anything that would be a real
 * problem if a determined user flipped a boolean in devtools must be enforced
 * in `permission.middleware.js`, and is.
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../../features/login/ux/hooks/useAuth';
import {
  NAV_GROUPS,
  SETTINGS_GROUPS,
  firstAllowedRoute,
  hasPermission,
} from '@shared/permissions';

export function usePermissions() {
  const { user } = useAuth();

  const permissions = useMemo(() => user?.permissions ?? [], [user]);

  /**
   * Memoised on the array identity rather than its contents. `permissions`
   * only changes when the auth state is refetched, so this is stable across
   * the renders that matter — and a `can()` that changed identity every render
   * would defeat every `useMemo` downstream that depends on it.
   */
  const can = useCallback(
    (permission: string) => hasPermission(permissions, permission),
    [permissions],
  );

  return useMemo(
    () => ({
      can,
      /** Shorthand for the common "is this screen visible at all" question. */
      canView: (group: string) => can(`${group}.view`),
      canManage: (group: string) => can(`${group}.manage`),
      isAdmin: Boolean(user?.isAdmin),
      /** Product ids the user may see revenue for; null means all of them. */
      productScope: user?.productScope ?? null,
      /** Nav rail entries this user can open, in order. */
      navGroups: NAV_GROUPS.filter((group) => can(`${group.key}.view`)),
      /** Settings tabs this user can open, in order. */
      settingsGroups: SETTINGS_GROUPS.filter((group) => can(`${group.key}.view`)),
      /** Where to send someone who landed somewhere they cannot be. */
      homeRoute: firstAllowedRoute(permissions),
    }),
    [can, permissions, user],
  );
}
